import { CameraIconNew, IconTrash, PencilSimpleLineIcon, UploadIcon, ViewPhoto } from '@/assets/images';
import Dropdown from '@/components/Dropdown';
import { UserProfile } from '@/features/users';
import { useLanguage } from '@/localization/useLanguage';
import PropTypes from 'prop-types';
import React from 'react';
import './AvatarPhotoPicker.scss';
import { useAvatarPhotoPicker } from './viewmodel/useAvatarPhotoPicker';

/**
 * View — AvatarPhotoPicker
 *
 * Pure presentation component — zero business logic.
 * WebCamera and CropperModal are opened via the global popupService
 * (handled entirely inside the ViewModel), so this View has no popup JSX at all.
 *
 * Uses existing app-level SCSS classes (SignInFlow.scss).
 */
const AvatarPhotoPickerBase = ({
   userId,
   name,
   initialImage = null,
   onChange,
   className,
   defaultIcon,
   showDropdown = true,
}) => {
   const { t } = useLanguage();
   const { state, refs, handlers } = useAvatarPhotoPicker({ initialImage, onChange });
   const { imageSrc } = state;
   const { fileInputRef } = refs;
   const { handleUploadClick, handleFileChange, handleOpenCamera, handleDelete, handleViewPhoto } = handlers;

   const dropdownItems = [
      ...(imageSrc
         ? [
              {
                 id: 'view',
                 icon: <ViewPhoto />,
                 label: t('user.VIEW_PHOTO'),
                 action: handleViewPhoto,
              },
           ]
         : []),
      {
         id: 'upload',
         icon: <UploadIcon />,
         label: t('user.UPLOAD_PHOTO'),
         action: handleUploadClick,
      },
      {
         id: 'take-profile-photo',
         icon: <CameraIconNew />,
         label: t('user.TAKE_PHOTO'),
         action: handleOpenCamera,
      },
      ...(imageSrc
         ? [
              {
                 id: 'delete',
                 icon: <IconTrash />,
                 label: t('user.DELETE'),
                 className: 'delete-item',
                 action: handleDelete,
              },
           ]
         : []),
   ];

   return (
      <>
         {/* Hidden file input — triggered programmatically */}
         <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
         />

         <div className="profile-avatar-container ct-relative">
            {/* Avatar preview */}
            <div className={`profile-avatar ct-flexCenter ${className}`}>
               <UserProfile
                  image={imageSrc}
                  userId={userId}
                  name={name}
                  className="profile-avatar chat-avatar-fallback ct-flexCenter"
                  defaultIcon={defaultIcon}
               />
            </div>

            {/* Pencil dropdown */}
            {showDropdown && (
               <div className="profile-edit-dropdown">
                  <Dropdown
                     icon={<PencilSimpleLineIcon />}
                     items={dropdownItems}
                     position="bottom"
                     buttonClassName="profile-edit-btn ct-flexCenter ct-cursor"
                     dropdownClassName="profile-dropdown-menu"
                     withPadding={false}
                  />
               </div>
            )}
         </div>
      </>
   );
};

AvatarPhotoPickerBase.propTypes = {
   /** Pre-filled image URL — synced on mount and whenever it changes */
   initialImage: PropTypes.string,
   /** Locally entered name for initials fallback */
   name: PropTypes.string,
   /** Called with (Blob|null) whenever user confirms a new image or deletes */
   onChange: PropTypes.func,
   userId: PropTypes.string,
   className: PropTypes.string,
   defaultIcon: PropTypes.node,
   showDropdown: PropTypes.bool,
};

export const AvatarPhotoPicker = React.memo(AvatarPhotoPickerBase);
